import { useEffect, useRef, useState } from "react";
import { Share2, Copy, Check, ChevronDown, Twitter, Image as ImageIcon, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { api } from "../lib/api";

const VERDICT_LABEL = {
  SAFE_TOGETHER: "No label conflict found",
  USE_WITH_CAUTION: "Use with caution",
  AVOID: "Avoid",
  INSUFFICIENT_DATA: "Not enough verified data",
  // Legacy verdict codes from older checks.
  SAFE: "No label conflict found",
  MOSTLY_SAFE: "No label conflict found",
  CAUTION: "Use with caution",
};

function slugify(s) {
  return (s || "result")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 60) || "result";
}

/**
 * ShareControls — primary "Share Result" button.
 *
 * Behavior (per launch spec):
 * a) navigator.share(...) when available (mobile + iOS Safari + some desktop)
 * b) Fallback popover: Copy link / X (Twitter) / Reddit
 * c) "Save image →" small text link below — downloads the OG card as PNG
 */
export default function ShareControls({ result }) {
  const historyId = result?.history_id || result?.check_history_id || result?.id;
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [comboCopied, setComboCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const popoverRef = useRef(null);
  const triggerRef = useRef(null);

  // Close popover on outside click
  useEffect(() => {
    if (!popoverOpen) return undefined;
    const handler = (e) => {
      if (
        popoverRef.current && !popoverRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) {
        setPopoverOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [popoverOpen]);

  if (!historyId) return null;

  const product = result.product_name || result.product?.name || "your product";
  const score = result.safety_score;
  const verdict = VERDICT_LABEL[result.verdict] || result.verdict || "your verdict";
  const meds = result.medications?.length
    ? result.medications.map((m) => m.name || m.generic_name || m).filter(Boolean)
    : (result.medication?.name ? [result.medication.name] : []);
  const medicationLabel = meds.join(" + ") || "your medication";

  // Build the canonical /r/{historyId} permalink so the shared link survives
  // outside the user's session (incognito, social previewers, embeds). Falls
  // back to result.share_url when the API supplies one explicitly.
  const shareUrl = result.share_url
    || `${window.location.origin}/r/${historyId}`;
  const shareTitle = score != null
    ? `${product} scored ${score}/100`
    : `${product} on Blendsafe`;
  const shareText = `I checked ${product} with Blendsafe while on ${medicationLabel} — ${verdict}. Check yours:`;
  const tweetText = `${shareText} ${shareUrl}`;

  const handleShare = async () => {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
        return;
      } catch (e) {
        // User dismissed — fall through to popover only if it's an actual error.
        if (e?.name === "AbortError") return;
      }
    }
    setPopoverOpen((v) => !v);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied to clipboard", {
        description: "Send it anywhere — anyone with the link can view this exact check.",
      });
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy that link", {
        description: "Long-press the URL in your browser bar to copy it manually.",
      });
    }
  };

  // "Share this exact combo" — builds a re-runnable deep-link
  //   /check?med=<medId>&product=<productId>
  // Only enabled when the result was produced from a CATALOG product (so the
  // receiver can re-run the same check). Ingredient-paste / barcode-only
  // checks fall back to the standard `/r/{historyId}` permalink above.
  const medSlug = result?.medication?.id || result?.medications?.[0]?.id || null;
  const productSlug = result?.product_id || null;
  const comboUrl = (medSlug && productSlug)
    ? `${window.location.origin}/check?med=${encodeURIComponent(medSlug)}&product=${encodeURIComponent(productSlug)}`
    : null;

  const handleCopyCombo = async () => {
    if (!comboUrl) return;
    try {
      await navigator.clipboard.writeText(comboUrl);
      setComboCopied(true);
      toast.success("Combo link copied", {
        description: "Anyone who taps it lands on this exact medication + product check.",
      });
      setTimeout(() => setComboCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy that link", {
        description: "Long-press the URL in your browser bar to copy it manually.",
      });
    }
  };

  const openTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    setPopoverOpen(false);
  };

  const openReddit = () => {
    const url =
      `https://reddit.com/submit?url=${encodeURIComponent(shareUrl)}` +
      `&title=${encodeURIComponent(shareTitle)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    setPopoverOpen(false);
  };

  const handleSaveImage = async (e) => {
    e.preventDefault();
    if (downloading) return;
    setDownloading(true);
    try {
      const { data } = await api.get(`/share/card/${historyId}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `blendsafe-${slugify(product)}-${score ?? "result"}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Couldn't save the image", {
        description: "Refresh the page and try again — your check is still saved.",
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="relative inline-flex flex-col items-start gap-2">
      <Button
        ref={triggerRef}
        type="button"
        data-testid="result-share-btn"
        variant="outline"
        onClick={handleShare}
        className="rounded-full h-11 border-surface-line bg-cream-50 hover:bg-cream-100 hover:border-ink/20 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
      >
        <Share2 className="w-4 h-4 mr-2" />
        Share Result
        <ChevronDown className="w-3.5 h-3.5 ml-1.5 opacity-60" />
      </Button>

      {popoverOpen && (
        <div
          ref={popoverRef}
          data-testid="share-popover"
          role="menu"
          className="absolute top-full left-0 mt-2 z-30 w-64 rounded-2xl bg-white border border-surface-line shadow-floating overflow-hidden"
        >
          <button
            type="button"
            data-testid="share-copy-btn"
            onClick={handleCopy}
            className="w-full flex items-center gap-3 px-4 py-3 text-[14px] text-ink hover:bg-cream-100 transition-colors text-left"
          >
            {copied ? (
              <Check className="w-4 h-4 text-emerald-600" />
            ) : (
              <Copy className="w-4 h-4 text-ink-mute" />
            )}
            {copied ? "Link copied!" : "Copy link"}
          </button>
          {comboUrl && (
            <button
              type="button"
              data-testid="share-combo-btn"
              onClick={handleCopyCombo}
              title="Anyone with this link will land directly on this exact check"
              className="w-full flex items-center gap-3 px-4 py-3 text-[14px] text-ink hover:bg-cream-100 transition-colors text-left border-t border-surface-line/70"
            >
              {comboCopied ? (
                <Check className="w-4 h-4 text-emerald-600" />
              ) : (
                <Share2 className="w-4 h-4 text-ink-mute" />
              )}
              <span className="flex-1">
                {comboCopied ? "Combo link copied!" : "Copy combo link"}
                <span className="block text-[11px] text-ink-mute mt-0.5">
                  Re-runs this exact check for anyone
                </span>
              </span>
            </button>
          )}
          <button
            type="button"
            data-testid="share-twitter-btn"
            onClick={openTwitter}
            className="w-full flex items-center gap-3 px-4 py-3 text-[14px] text-ink hover:bg-cream-100 transition-colors text-left border-t border-surface-line/70"
          >
            <Twitter className="w-4 h-4 text-ink-mute" />
            Share on X (Twitter)
            <ExternalLink className="w-3 h-3 ml-auto opacity-50" />
          </button>
          <button
            type="button"
            data-testid="share-reddit-btn"
            onClick={openReddit}
            className="w-full flex items-center gap-3 px-4 py-3 text-[14px] text-ink hover:bg-cream-100 transition-colors text-left border-t border-surface-line/70"
          >
            <span className="inline-flex w-4 h-4 items-center justify-center rounded-full bg-ink-mute text-[10px] text-white font-bold">
              r/
            </span>
            Share on Reddit
            <ExternalLink className="w-3 h-3 ml-auto opacity-50" />
          </button>
        </div>
      )}

      <button
        type="button"
        data-testid="share-save-image-link"
        onClick={handleSaveImage}
        disabled={downloading}
        className="inline-flex items-center gap-1.5 text-[12.5px] text-ink-mute hover:text-ink transition-colors disabled:opacity-60 px-1"
      >
        <ImageIcon className="w-3.5 h-3.5" />
        {downloading ? "Saving…" : "Save image →"}
      </button>
    </div>
  );
}
